/**
 * Money: always work with integers (paise) or Decimal strings internally.
 * Never use JS float for arithmetic — pass strings from DB.
 * Display helpers below are for UI only.
 */

/**
 * Round up to nearest integer (ceil)
 * Use this when you need the rounded number for calculations or display
 */
export function roundUp(value: number | string): number {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return isNaN(num) ? 0 : Math.ceil(num);
}

/**
 * Round up and format as Indian currency: ₹1,28,500
 */
export function formatINR(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '₹0';

  // ✅ Round UP to next integer (ceil)
  const rounded = Math.ceil(num);

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
}

/** Format a number as short Indian currency: ₹1.28L, ₹42K */
export function formatINRShort(value: number): string {
  const rounded = Math.ceil(value);

  if (rounded >= 10_000_000) return `₹${(rounded / 10_000_000).toFixed(2)}Cr`;
  if (rounded >= 100_000) return `₹${(rounded / 100_000).toFixed(2)}L`;
  if (rounded >= 1_000) return `₹${(rounded / 1_000).toFixed(1)}K`;
  return `₹${rounded}`;
}

/**
 * Safe multiply: qty × rate without float drift.
 * Rounds to 2 decimal places.
 */
export function calcAmount(qty: number, rate: number): number {
  return Math.round(qty * rate * 100) / 100;
}

/**
 * Date: store UTC, display IST (Asia/Kolkata)
 */
const IST_LOCALE = 'en-IN';
const IST_TZ = 'Asia/Kolkata';

export function formatDateIST(date?: string | Date | null): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat(IST_LOCALE, {
    timeZone: IST_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

export function formatDateTimeIST(date?: string | Date | null): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat(IST_LOCALE, {
    timeZone: IST_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Convert a Date to YYYY-MM-DD string for input[type=date] */
// Local calendar date as YYYY-MM-DD (toISOString would shift it to UTC and can show the previous day)
export function toInputDate(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Calendar date (YYYY-MM-DD, local) of a value from the API. Date columns arrive as an ISO
// timestamp at local midnight, so cutting the string at 'T' can return the previous day.
export function toLocalDate(value?: string | Date | null): string {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : toInputDate(d);
}
